import { useEffect, useId, useState, type ReactElement, type ReactNode } from 'react';

import { ConfirmDialog } from '@/components/admin/ConfirmDialog';
import { Pagination } from '@/components/admin/Pagination';
import { EmptyState, ErrorState, ForbiddenState, SkeletonRows } from '@/components/admin/states';

/**
 * WP風 List Table（指示書 §7-1）。
 *
 * - 上部に「新規追加」＋ステータスタブ（件数付き）＋検索ボックス
 * - 一括操作ドロップダウン＋チェックボックス列
 * - ページネーション上下
 * - 行のインラインアクション（詳細|解除 など）は**常時表示**（D-080）。
 *   `destructive` を付けた操作は**この部品が確認モーダルを挟む**（§7-3 / D-083）
 *
 * 一覧画面はこの部品を使い回し、新しいUIパターンを作らないこと。
 */

export interface Column<T> {
  key: string;
  header: ReactNode;
  /** 主要列（行の先頭・太字。インラインアクションはこの列に出る） */
  primary?: boolean;
  className?: string;
  render: (row: T) => ReactNode;
}

export interface StatusTab {
  value: string | null;
  label: string;
  count?: number | undefined;
}

export interface RowAction<T> {
  label: string;
  onSelect: (row: T) => void;
  /** 破壊的操作は確認モーダルを挟むこと（§7-3） */
  destructive?: boolean;
}

export interface BulkAction {
  value: string;
  label: string;
}

export type ListState = 'ready' | 'loading' | 'error' | 'forbidden';

export interface ListTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  state?: ListState | undefined;
  errorMessage?: string | undefined;
  onRetry?: (() => void) | undefined;

  /** ステータスタブ（すべて | 公開済み | 下書き …） */
  tabs?: StatusTab[] | undefined;
  activeTab?: string | null | undefined;
  onTabChange?: ((value: string | null) => void) | undefined;

  searchValue?: string | undefined;
  onSearch?: ((value: string) => void) | undefined;
  searchPlaceholder?: string | undefined;

  bulkActions?: BulkAction[] | undefined;
  onBulkApply?: ((action: string, keys: string[]) => void) | undefined;

  rowActions?: RowAction<T>[] | undefined;

  page?: number | undefined;
  perPage?: number | undefined;
  total?: number | undefined;
  onPageChange?: ((page: number) => void) | undefined;

  addLabel?: string | undefined;
  onAdd?: (() => void) | undefined;

  emptyTitle?: string | undefined;
  emptyDescription?: string | undefined;
  emptyAction?: ReactNode | undefined;

  /** タブ直下の補足（選択中のタブが何かを説明する） */
  hint?: ReactNode | undefined;

  /** 確認モーダルで「どの行に対する操作か」を示すための表示名（D-083） */
  rowLabel?: ((row: T) => string) | undefined;

  /** 行ごとの追加クラス（下書き／公開の色分けなど） */
  rowClassName?: ((row: T) => string | undefined) | undefined;

  /** false を返した行は一括選択の対象外（D-363） */
  isRowSelectable?: ((row: T) => boolean) | undefined;

  /** 変わったタイミングで選択状態をクリアする */
  clearSelectionToken?: number | undefined;

  caption: string;
}

export function ListTable<T>({
  rows,
  columns,
  rowKey,
  state = 'ready',
  errorMessage,
  onRetry,
  tabs,
  activeTab = null,
  onTabChange,
  searchValue,
  onSearch,
  searchPlaceholder = '検索',
  bulkActions,
  onBulkApply,
  rowActions,
  page = 1,
  perPage = 20,
  total = rows.length,
  onPageChange,
  addLabel,
  onAdd,
  emptyTitle = '該当するデータがありません',
  emptyDescription,
  emptyAction,
  hint,
  rowLabel,
  rowClassName,
  isRowSelectable,
  clearSelectionToken,
  caption,
}: ListTableProps<T>): ReactElement {
  // destructive な行操作は、押した瞬間に実行せず必ず確認を挟む（D-083）
  const [pending, setPending] = useState<{ action: RowAction<T>; row: T } | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkAction, setBulkAction] = useState('');
  const [draftSearch, setDraftSearch] = useState(searchValue ?? '');
  const searchId = useId();
  const bulkId = useId();

  const selectable = Boolean(bulkActions?.length);
  const selectableKeys = rows
    .filter((row) => isRowSelectable?.(row) !== false)
    .map(rowKey);
  const allSelected =
    selectableKeys.length > 0 && selectableKeys.every((key) => selected.includes(key));

  useEffect(() => {
    if (clearSelectionToken !== undefined) {
      setSelected([]);
      setBulkAction('');
    }
  }, [clearSelectionToken]);

  function toggleAll(checked: boolean): void {
    setSelected(checked ? selectableKeys : []);
  }

  function toggleOne(key: string, checked: boolean): void {
    setSelected((prev) => (checked ? [...prev, key] : prev.filter((k) => k !== key)));
  }

  const columnCount = columns.length + (selectable ? 1 : 0);
  // 中身の無いバーは描画しない（空の帯が「意味のない二重線」に見える / 利用者フィードバック）
  const hasToolbar = Boolean(onAdd) || Boolean(tabs && tabs.length > 0) || Boolean(onSearch);
  const hasBulkBar = selectable || Boolean(onPageChange);

  return (
    <div className="rounded-sm border border-wpborder bg-white">
      {/* 上部バー: 新規追加・ステータスタブ・検索 */}
      {hasToolbar && (
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-wpborder px-4 py-2.5">
        <div className="flex flex-wrap items-center gap-3">
          {onAdd && (
            <button
              type="button"
              onClick={onAdd}
              className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover"
            >
              {addLabel ?? '新規追加'}
            </button>
          )}
          {tabs && tabs.length > 0 && (
            <ul className="flex flex-wrap items-center gap-x-2 text-wptext-muted">
              {tabs.map((tab, index) => (
                <li key={tab.label} className="flex items-center gap-2">
                  {index > 0 && <span aria-hidden="true">|</span>}
                  <button
                    type="button"
                    aria-current={activeTab === tab.value ? 'page' : undefined}
                    onClick={() => onTabChange?.(tab.value)}
                    className={
                      activeTab === tab.value
                        ? 'font-semibold text-wptext-strong'
                        : 'text-wpaccent hover:text-wpaccent-hover'
                    }
                  >
                    {tab.label}
                    {tab.count !== undefined && (
                      <span className="ml-1 text-wptext-muted">({tab.count})</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {onSearch && (
          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              onSearch(draftSearch);
            }}
          >
            <label htmlFor={searchId} className="sr-only">
              {searchPlaceholder}
            </label>
            <input
              id={searchId}
              type="search"
              value={draftSearch}
              placeholder={searchPlaceholder}
              onChange={(event) => setDraftSearch(event.target.value)}
              className="rounded-sm border border-wpborder px-2.5 py-1.5"
            />
            <button
              type="submit"
              className="btn-secondary"
            >
              検索
            </button>
          </form>
        )}
      </div>
      )}
      {hint && (
        <p className="border-b border-wpborder bg-wpcanvas/40 px-4 py-2.5 text-wptext-muted">{hint}</p>
      )}

      {/* 一括操作 + 上部ページネーション */}
      {hasBulkBar && (
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-wpborder px-4 py-2.5">
        {selectable ? (
          <div className="flex items-center gap-2">
            <label htmlFor={bulkId} className="sr-only">
              一括操作
            </label>
            <select
              id={bulkId}
              value={bulkAction}
              onChange={(event) => setBulkAction(event.target.value)}
              className="rounded-sm border border-wpborder px-2.5 py-1.5"
            >
              <option value="">一括操作</option>
              {bulkActions?.map((action) => (
                <option key={action.value} value={action.value}>
                  {action.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={!bulkAction || selected.length === 0}
              onClick={() => onBulkApply?.(bulkAction, selected)}
              className="btn-secondary"
            >
              適用
            </button>
            {selected.length > 0 && (
              <span className="text-wptext-muted">{selected.length}件選択中</span>
            )}
          </div>
        ) : (
          <span />
        )}
        {onPageChange && (
          <Pagination
            page={page}
            perPage={perPage}
            total={total}
            onChange={onPageChange}
            label="ページ送り（上）"
          />
        )}
      </div>
      )}

      {/* 本体 */}
      {state === 'loading' && <SkeletonRows columns={columnCount} />}
      {state === 'error' && <ErrorState message={errorMessage} onRetry={onRetry} />}
      {state === 'forbidden' && <ForbiddenState />}
      {state === 'ready' && rows.length === 0 && (
        <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
      )}
      {state === 'ready' && rows.length > 0 && (
        <table className="w-full border-collapse text-left text-wptable">
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="border-b border-wpborder bg-wpcanvas/60">
              {selectable && (
                <th scope="col" className="w-10 px-5 py-3.5">
                  <input
                    type="checkbox"
                    aria-label="すべて選択"
                    checked={allSelected}
                    onChange={(event) => toggleAll(event.target.checked)}
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`px-5 py-3.5 font-semibold text-wptext-strong ${column.className ?? ''}`}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const key = rowKey(row);
              const rowSelectable = isRowSelectable?.(row) !== false;
              return (
                <tr
                  key={key}
                  className={`group border-b border-wpborder last:border-b-0 hover:bg-wpcanvas/60 ${rowClassName?.(row) ?? ''}`}
                >
                  {selectable && (
                    <td className="px-5 py-4 align-middle">
                      {rowSelectable ? (
                        <input
                          type="checkbox"
                          aria-label="この行を選択"
                          checked={selected.includes(key)}
                          onChange={(event) => toggleOne(key, event.target.checked)}
                        />
                      ) : null}
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className={`px-5 py-4 align-middle ${column.className ?? ''} ${
                        column.primary ? 'font-semibold text-wptext-strong' : ''
                      }`}
                    >
                      {column.render(row)}
                      {column.primary && rowActions && rowActions.length > 0 && (
                        // 常時表示にする。ホバーで出す方式は「どこに操作があるか
                        // 分からない」うえ、この行はもともと高さを確保しているので
                        // 表示してもレイアウトはずれない（利用者フィードバック）
                        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                          {rowActions.map((action, index) => (
                            <span key={action.label} className="flex items-center gap-2">
                              {index > 0 && (
                                <span aria-hidden="true" className="text-wptext-muted">
                                  |
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() =>
                                  action.destructive
                                    ? setPending({ action, row })
                                    : action.onSelect(row)
                                }
                                className={
                                  action.destructive
                                    ? 'text-wpnotice-error hover:underline'
                                    : 'text-wpaccent hover:underline'
                                }
                              >
                                {action.label}
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* 下部ページネーション */}
      {onPageChange && state === 'ready' && rows.length > 0 && (
        <div className="border-t border-wpborder">
          <Pagination
            page={page}
            perPage={perPage}
            total={total}
            onChange={onPageChange}
            label="ページ送り（下）"
          />
        </div>
      )}

      <ConfirmDialog
        open={pending !== null}
        title={pending ? `${pending.action.label}` : ''}
        // ラベルは「取り消す」「解除」など動詞・名詞が混在するため、
        // 「〜します」と繋げず「〜を実行します」の形にする（D-083）
        message={
          pending
            ? `${rowLabel ? `「${rowLabel(pending.row)}」` : 'この行'}に対して「${pending.action.label}」を実行します。よろしいですか？`
            : ''
        }
        confirmLabel={pending?.action.label ?? '実行する'}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          const target = pending;
          setPending(null);
          target?.action.onSelect(target.row);
        }}
      />
    </div>
  );
}
